import { useFeatureGate } from '@statsig/react-bindings';
import { useCallback } from 'react';
import { useLocalStorage } from 'usehooks-ts';

import { AppConfig, DEV_APP_SETTINGS_KEY, IS_DEV_APP_SETTINGS_ENABLED_KEY, getDevAppSettings } from '../config';
import { Statsig } from '../statsig/StatsigProvider';
import { enableZoomGate } from '../statsig/gates';
import { VIDEO_PLATFORMS, VideoPlatform } from '../video-sdk';

/**
 * Determines if Zoom video integration is enabled for the current user.
 * This hook implements a provider-dependent feature pattern where patient access
 * to Zoom functionality depends on their provider's Statsig allocation.
 *
 * Uses a feature gate with "providerShortID" (case-sensitive) as the
 * Statsig ID Type, defined as a customID on the StatsigUser.
 * @see gracefullyUpdateStatsigUser for user configuration details
 */
export function useIsZoomEnabled(): boolean {
  const { value: isZoomGateEnabled } = useFeatureGate(enableZoomGate);
  const [isDevAppSettingsEnabled] = useLocalStorage<number>(IS_DEV_APP_SETTINGS_ENABLED_KEY, 0);
  const [devAppSettings] = useLocalStorage<Partial<AppConfig>>(DEV_APP_SETTINGS_KEY, {});

  const hasDevVideoPlatformOverride =
    Boolean(isDevAppSettingsEnabled) && VIDEO_PLATFORMS.has(devAppSettings?.videoPlatformOverride as VideoPlatform);

  return checkZoomIsEnabled({
    hasDevVideoPlatformOverride,
    devAppSettings,
    checkStatsig: () => {
      return isZoomGateEnabled;
    },
  });
}

export function useIsZoomEnabledCallback(): () => boolean {
  return useCallback(() => {
    const devAppSettings = getDevAppSettings();
    const hasDevVideoPlatformOverride =
      localStorage.getItem(IS_DEV_APP_SETTINGS_ENABLED_KEY) === '1' &&
      VIDEO_PLATFORMS.has(devAppSettings?.videoPlatformOverride as VideoPlatform);

    return checkZoomIsEnabled({
      hasDevVideoPlatformOverride,
      devAppSettings,
      checkStatsig: () => {
        return Statsig.checkGate(enableZoomGate);
      },
    });
  }, []);
}

type CheckZoomIsEnabledProps = {
  hasDevVideoPlatformOverride: boolean;
  devAppSettings: Partial<AppConfig>;
  checkStatsig: () => boolean;
};

function checkZoomIsEnabled({ hasDevVideoPlatformOverride, devAppSettings, checkStatsig }: CheckZoomIsEnabledProps) {
  let isZoomEnabled: boolean;

  if (hasDevVideoPlatformOverride) {
    // if dev video platform override is set, use it
    isZoomEnabled = devAppSettings?.videoPlatformOverride === VideoPlatform.Zoom;
  } else {
    // otherwise, use statsig gate
    isZoomEnabled = checkStatsig();
  }

  return isZoomEnabled;
}
